import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Chip, Divider, LinearProgress,
  Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Stepper, Step, StepLabel,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { waveService } from '../services/waveService';
import { WMS_TYPE_CHIP_SX } from '../shared/wmsTypeChip';

interface WaveOrder {
  orderId: string;
  orderNumber: string;
  customerName: string;
  orderType: string;
  itemCount: number;
  status: string;
}

interface PickListItem {
  orderItemId: string;
  orderId: string;
  productId: string;
  productName: string;
  sku: string;
  barcode: string;
  quantity: number;
  pickedQuantity: number;
  locationCode: string;
  locationName: string;
  sortOrder: number;
  status: string;
  aisleName: string;
}

interface WaveDetail {
  id: string;
  waveNumber: string;
  waveType: string;
  status: string;
  assignedPickerName: string;
  cartCode: string;
  routingStrategy: string;
  totalOrders: number;
  totalItems: number;
  pickedItems: number;
  estimatedDistance: number;
  createdAt: string;
  startedAt: string;
  completedAt: string;
  orders: WaveOrder[];
}

interface WaveDetailModalProps {
  open: boolean;
  waveId: string | null;
  onClose: () => void;
}

const STEPS = ['Oluşturuldu', 'Devam Ediyor', 'Tamamlandı'];

function statusToStep(status: string): number {
  switch (status) {
    case 'Created': return 0;
    case 'InProgress': return 1;
    case 'Completed': return 2;
    case 'Cancelled': return -1;
    default: return 0;
  }
}

function formatElapsed(startedAt: string): string {
  const start = new Date(startedAt).getTime();
  const diff = Date.now() - start;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}dk`;
  const hours = Math.floor(mins / 60);
  return `${hours}sa ${mins % 60}dk`;
}

const PICK_STATUS_ICON: Record<string, React.ReactNode> = {
  Picked: <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />,
  Pending: <RadioButtonUncheckedIcon sx={{ color: 'text.disabled', fontSize: 16 }} />,
  InProgress: <PlayCircleOutlineIcon sx={{ color: 'primary.main', fontSize: 16 }} />,
  ShortPick: <ErrorOutlineIcon sx={{ color: 'warning.main', fontSize: 16 }} />,
};

const WaveDetailModal: React.FC<WaveDetailModalProps> = ({ open, waveId, onClose }) => {
  const [detail, setDetail] = useState<WaveDetail | null>(null);
  const [pickList, setPickList] = useState<PickListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickListLoading, setPickListLoading] = useState(false);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [elapsed, setElapsed] = useState('');

  const loadDetail = useCallback(async () => {
    if (!waveId) return;
    setLoading(true);
    try {
      const res = await waveService.getDetail(waveId);
      const d = res.data?.data || res.data;
      setDetail(d);
    } catch { /* ignore */ }
    setLoading(false);
  }, [waveId]);

  const loadPickList = useCallback(async () => {
    if (!waveId) return;
    setPickListLoading(true);
    try {
      const res = await waveService.getPickList(waveId);
      const items = res.data?.data || res.data || [];
      setPickList(Array.isArray(items) ? items : []);
    } catch { setPickList([]); }
    setPickListLoading(false);
  }, [waveId]);

  useEffect(() => {
    if (open && waveId) {
      loadDetail();
      loadPickList();
    }
    return () => {
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    };
  }, [open, waveId, loadDetail, loadPickList]);

  useEffect(() => {
    if (elapsedRef.current) clearInterval(elapsedRef.current);
    if (detail?.status === 'InProgress' && detail.startedAt) {
      setElapsed(formatElapsed(detail.startedAt));
      elapsedRef.current = setInterval(() => {
        setElapsed(formatElapsed(detail.startedAt));
      }, 30000);
    }
  }, [detail]);

  const pct = detail && detail.totalItems > 0
    ? Math.round((detail.pickedItems / detail.totalItems) * 100) : 0;
  const activeStep = detail ? statusToStep(detail.status) : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        İş Emri Detayı {detail ? `- ${detail.waveNumber}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : detail ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Stepper */}
            {detail.status !== 'Cancelled' ? (
              <Stepper activeStep={activeStep} alternativeLabel>
                {STEPS.map(label => (
                  <Step key={label}><StepLabel>{label}</StepLabel></Step>
                ))}
              </Stepper>
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <Chip label="✕ İptal Edildi" color="error" />
              </Box>
            )}

            {/* General Info */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Tip</Typography>
                <Box><Chip label={detail.waveType === 'Single' ? 'Tekli' : 'Çoklu'} size="small"
                  color={detail.waveType === 'Single' ? 'info' : 'secondary'} sx={WMS_TYPE_CHIP_SX} /></Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Toplayıcı</Typography>
                <Typography variant="body2" fontWeight={500}>{detail.assignedPickerName || 'Atanmamış'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Araba</Typography>
                <Typography variant="body2" fontWeight={500}>{detail.cartCode || 'Atanmamış'}</Typography>
              </Box>
              {detail.routingStrategy && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Strateji</Typography>
                  <Typography variant="body2">{detail.routingStrategy}</Typography>
                </Box>
              )}
              {detail.estimatedDistance != null && detail.estimatedDistance > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Tah. Mesafe</Typography>
                  <Typography variant="body2">{detail.estimatedDistance}m</Typography>
                </Box>
              )}
              {detail.startedAt && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Başlama</Typography>
                  <Typography variant="body2">
                    {new Date(detail.startedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              )}
              {detail.status === 'InProgress' && elapsed && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Geçen Süre</Typography>
                  <Typography variant="body2" fontWeight={500}>{elapsed}</Typography>
                </Box>
              )}
              {detail.completedAt && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Tamamlanma</Typography>
                  <Typography variant="body2">
                    {new Date(detail.completedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Progress */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">İlerleme</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {detail.pickedItems}/{detail.totalItems} ürün ({pct}%)
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={pct}
                sx={{ height: 10, borderRadius: 5 }}
                color={pct === 100 ? 'success' : 'primary'} />
            </Box>

            <Divider />

            {/* Orders */}
            <Typography variant="subtitle2">Siparişler ({detail.totalOrders})</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sipariş No</TableCell>
                  <TableCell>Müşteri</TableCell>
                  <TableCell align="center">Ürün</TableCell>
                  <TableCell>Durum</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detail.orders || []).map(o => (
                  <TableRow key={o.orderId}>
                    <TableCell><Typography variant="body2" fontWeight={500}>{o.orderNumber}</Typography></TableCell>
                    <TableCell>{o.customerName}</TableCell>
                    <TableCell align="center">{o.itemCount}</TableCell>
                    <TableCell>
                      <Chip label={o.status === 'Picking' ? 'Toplanıyor' : o.status === 'Packed' ? 'Paketlendi' : o.status}
                        size="small" color={o.status === 'Picking' ? 'primary' : 'default'} variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider />

            {/* Pick List */}
            <Typography variant="subtitle2">Toplama Listesi (SortOrder sıralı)</Typography>
            {pickListLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">#</TableCell>
                    <TableCell>Lokasyon</TableCell>
                    <TableCell>Ürün</TableCell>
                    <TableCell align="center">Miktar</TableCell>
                    <TableCell align="center">Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pickList.map((item, idx) => {
                    const isShortPick = item.status === 'ShortPick';
                    const isPicked = !isShortPick && item.pickedQuantity >= item.quantity;
                    // "Sıradaki" = henüz toplanmamış ve short-pick de edilmemiş ilk kalem.
                    const isNext = !isPicked && !isShortPick
                      && idx === pickList.findIndex(p => p.status !== 'ShortPick' && p.pickedQuantity < p.quantity);
                    const iconKey = isShortPick ? 'ShortPick' : isPicked ? 'Picked' : isNext ? 'InProgress' : 'Pending';
                    return (
                      <TableRow key={item.orderItemId}
                        sx={{
                          bgcolor: isNext ? 'action.selected' : isPicked ? 'action.hover' : 'inherit',
                          opacity: isPicked ? 0.7 : 1,
                        }}>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={isNext ? 700 : 400}>{idx + 1}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{item.locationCode || '-'}</Typography>
                          {item.aisleName && (
                            <Typography variant="caption" color="text.secondary">{item.aisleName}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{item.productName || item.sku || item.barcode}</Typography>
                        </TableCell>
                        <TableCell align="center">{item.pickedQuantity}/{item.quantity}</TableCell>
                        <TableCell align="center">
                          {PICK_STATUS_ICON[iconKey]}
                          {isShortPick && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', lineHeight: 1 }}>
                              Stok Yok
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {pickList.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">Toplama listesi boş</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Box>
        ) : (
          <Typography color="text.secondary">Yükleniyor...</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaveDetailModal;
